/**
 * export-findings.ts — Export findings to CSV.
 *
 * Client-side CSV generation from findings data. Downloads a .csv file
 * that opens cleanly in Excel, Google Sheets, or any spreadsheet app.
 *
 * Columns: Loan Number, Rule ID, Point, Severity, Status, Verdict Source,
 *          Title, Description, Created At
 */

import type { FindingDTO } from '@loanaudit/shared';

/** Export a list of findings as a CSV download. */
export function exportFindingsToCSV(
  findings: Array<FindingDTO & { loanNumber?: string }>,
  filename = 'loan-audit-findings.csv',
): void {
  const headers = [
    'Loan Number',
    'Rule ID',
    'UC',
    'Severity',
    'Status',
    'Verdict Source',
    'Title',
    'Description',
    'Confidence',
    'Created At',
  ];

  const rows = findings.map((f) => [
    f.loanNumber ?? f.loanFileId,
    f.ruleId,
    f.ucId.toUpperCase(),
    f.severity,
    f.status,
    f.verdictSource,
    f.title,
    f.description,
    String(Math.round(f.confidence * 100)) + '%',
    '',
  ]);

  const csvContent = [headers, ...rows]
    .map((row) =>
      row.map((cell) => {
        // Escape cells that contain commas, quotes, or newlines
        const str = String(cell ?? '');
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(','),
    )
    .join('\r\n');

  // BOM prefix for Excel to recognize UTF-8
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
